import { useId, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

interface UploadTileProps {
  inputId: string
  title: string
  required?: boolean
  /** Shown in the tile until a preview exists. */
  icon: ReactNode
  /** Uploaded file name from the form value; empty when nothing is uploaded. */
  fileName: string
  previewUrl: string | null
  uploading: boolean
  error: string | null
  /** Present only when the last failure can be retried with the same file. */
  onRetry?: () => void
  /** Draft locked: the tile shows the upload but cannot change it. */
  locked: boolean
  onFile: (file: File) => void
}

/** Large tap target for one KYC document. The whole tile is the file input's label. */
export function UploadTile({
  inputId,
  title,
  required,
  icon,
  fileName,
  previewUrl,
  uploading,
  error,
  onRetry,
  locked,
  onFile,
}: UploadTileProps) {
  const { t } = useTranslation()
  const baseId = useId()
  const titleId = `${baseId}-title`
  const statusId = `${baseId}-status`
  const errorId = `${baseId}-error`
  const uploaded = Boolean(fileName)
  const disabled = locked || uploading

  const primary = uploading ? t('form.uploading') : uploaded ? t('form.uploaded') : t('form.chooseFile')
  const secondary = uploading ? null : uploaded ? fileName : t('form.noFileChosen')

  return (
    <div className="prod-upload">
      <p id={titleId} className={cn('prod-field__label', required && 'prod-field__label--required')}>
        {title}
      </p>
      <label
        htmlFor={inputId}
        className={cn(
          'prod-upload__tile',
          uploaded && 'prod-upload__tile--done',
          error && 'prod-upload__tile--error',
          disabled && 'prod-upload__tile--disabled',
        )}
        aria-busy={uploading || undefined}
      >
        <input
          id={inputId}
          type="file"
          className="prod-file__input"
          accept=".png,.jpg,.jpeg,image/png,image/jpeg"
          disabled={disabled}
          aria-labelledby={`${titleId} ${statusId}`}
          aria-describedby={error ? errorId : undefined}
          aria-invalid={error ? true : undefined}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (!file) return
            onFile(file)
            e.target.value = ''
          }}
        />
        <span className="prod-upload__media" aria-hidden="true">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="prod-upload__preview" referrerPolicy="no-referrer" />
          ) : (
            icon
          )}
        </span>
        <span className="prod-upload__text">
          <span id={statusId} className="prod-upload__primary" role={uploaded ? 'status' : undefined}>
            {uploaded && !uploading ? <Check className="prod-upload__ok size-4" aria-hidden="true" /> : null}
            {primary}
          </span>
          {secondary ? (
            <span className={cn('prod-upload__secondary', uploaded && 'prod-upload__secondary--file')} title={uploaded ? fileName : undefined}>
              {secondary}
            </span>
          ) : null}
          {uploaded && !locked && !uploading ? (
            <span className="prod-upload__action" aria-hidden="true">
              {t('form.replaceFile')}
            </span>
          ) : null}
        </span>
        {uploading ? (
          <span className="prod-upload__progress" aria-hidden="true">
            <span className="prod-upload__progress-bar" />
          </span>
        ) : null}
      </label>
      {error ? (
        <p id={errorId} className="prod-error" role="alert">
          {error}{' '}
          {onRetry ? (
            <button type="button" className="prod-link prod-link-btn" onClick={onRetry}>
              {t('form.retryUpload')}
            </button>
          ) : null}
        </p>
      ) : null}
    </div>
  )
}
